import { Temporal } from '@insio/dates';

/** Status of a code-list record on one day; deactivation wins over validity. */
export const LifecycleStatus = {
  ACTIVE: 'ACTIVE',
  DEACTIVATED: 'DEACTIVATED',
  OUT_OF_VALIDITY: 'OUT_OF_VALIDITY'
} as const;

export type LifecycleStatus = (typeof LifecycleStatus)[keyof typeof LifecycleStatus];

/** A status filter: one status, every record, or every record that is not active. */
export const LifecycleStatusFilter = {
  ALL: 'ALL',
  INACTIVE: 'INACTIVE',
  ...LifecycleStatus
} as const;

export type LifecycleStatusFilter =
  (typeof LifecycleStatusFilter)[keyof typeof LifecycleStatusFilter];

/** Active or not: deactivated and out-of-validity records both count as inactive. */
export const ACTIVE_OR_INACTIVE_FILTERS = [
  LifecycleStatusFilter.ACTIVE,
  LifecycleStatusFilter.INACTIVE
] as const;

/** The lifecycle fields every code list loads. */
export interface LifecycleRecord {
  readonly isActive: boolean;
  readonly validFrom?: Temporal.PlainDate | null;
  readonly validTo?: Temporal.PlainDate | null;
}

export type LifecycleState =
  | { readonly status: typeof LifecycleStatus.ACTIVE | typeof LifecycleStatus.DEACTIVATED }
  | {
      readonly status: typeof LifecycleStatus.OUT_OF_VALIDITY;
      /** `from` when validity has not started yet, `to` when it has ended. */
      readonly bound: 'from' | 'to';
      readonly date: Temporal.PlainDate;
    };

/** Status of `record` on `today`: deactivated first, then the inclusive validity window. */
export function lifecycleState(record: LifecycleRecord, today: Temporal.PlainDate): LifecycleState {
  if (!record.isActive) return { status: LifecycleStatus.DEACTIVATED };
  if (record.validFrom && Temporal.PlainDate.compare(today, record.validFrom) < 0) {
    return { status: LifecycleStatus.OUT_OF_VALIDITY, bound: 'from', date: record.validFrom };
  }
  if (record.validTo && Temporal.PlainDate.compare(today, record.validTo) > 0) {
    return { status: LifecycleStatus.OUT_OF_VALIDITY, bound: 'to', date: record.validTo };
  }
  return { status: LifecycleStatus.ACTIVE };
}

/** Whether `record` belongs under `filter` on `today`. */
export function matchesLifecycleFilter(
  record: LifecycleRecord,
  filter: LifecycleStatusFilter,
  today: Temporal.PlainDate
): boolean {
  if (filter === LifecycleStatusFilter.ALL) return true;
  const { status } = lifecycleState(record, today);
  return filter === LifecycleStatusFilter.INACTIVE
    ? status !== LifecycleStatus.ACTIVE
    : status === filter;
}

/** Whether the window ends before it starts; an open bound never inverts it. */
export function isValidityInverted(
  validFrom: Temporal.PlainDate | null | undefined,
  validTo: Temporal.PlainDate | null | undefined
): boolean {
  return !!validFrom && !!validTo && Temporal.PlainDate.compare(validTo, validFrom) < 0;
}

/** Whether two optional days are the same day; two empty bounds are equal. */
export function isSameDay(
  a: Temporal.PlainDate | null | undefined,
  b: Temporal.PlainDate | null | undefined
): boolean {
  return a && b ? a.equals(b) : !a && !b;
}
